import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { VenueQuickFilter } from './VenueQuickFilter';
import {
  EventStatus,
  EventVisibility,
  filterFormSchema,
  type FilterFormValues,
} from './schemas';
import type { FilterSheetProps } from './types';

const visibilityOptions = [
  { value: EventVisibility.PUBLIC, label: 'Public' },
  { value: EventVisibility.MEMBERS_ONLY, label: 'Members Only' },
];

const statusOptions = [
  { value: EventStatus.DRAFT, label: 'Draft' },
  { value: EventStatus.PUBLISHED, label: 'Published' },
];

export function FilterSheet({
  isOpen,
  setIsOpen,
  filters,
  onApplyFilters,
  onClearFilters,
}: FilterSheetProps) {
  const form = useForm<FilterFormValues>({
    resolver: zodResolver(filterFormSchema),
    defaultValues: {
      venueId: filters.venueId || '',
      visibility: filters.visibility || [],
      status: filters.status || [],
    },
  });

  // Reset form when sheet opens with current filter values
  useEffect(() => {
    if (isOpen) {
      form.reset({
        venueId: filters.venueId || '',
        visibility: filters.visibility || [],
        status: filters.status || [],
      });
    }
  }, [isOpen, filters, form]);

  const onSubmit = (values: FilterFormValues) => {
    onApplyFilters({
      venueId: values.venueId || '',
      visibility: values.visibility || [],
      status: values.status || [],
    });
    setIsOpen(false);
  };

  const handleClear = () => {
    form.reset({
      venueId: '',
      visibility: [],
      status: [],
    });
    onClearFilters();
    setIsOpen(false);
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>Filter Events</SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                <div className="space-y-6 py-3">
                  {/* Venue Filter */}
                  <FormField
                    control={form.control}
                    name="venueId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Venue</FormLabel>
                        <FormControl>
                          <VenueQuickFilter
                            venueFilter={field.value || ''}
                            handleChangeVenue={field.onChange}
                            triggerClassName="w-full"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <Separator
                    className="my-4 h-px bg-zinc-200"
                    orientation="horizontal"
                  />

                  {/* Visibility Filter */}
                  <FormField
                    control={form.control}
                    name="visibility"
                    render={() => (
                      <FormItem>
                        <FormLabel className="mb-3 block">Visibility</FormLabel>
                        <div className="space-y-3">
                          {visibilityOptions.map((option) => (
                            <FormField
                              key={option.value}
                              control={form.control}
                              name="visibility"
                              render={({ field }) => {
                                const checkboxId = `visibility-${option.value}`;
                                return (
                                  <FormItem className="flex items-center gap-2">
                                    <FormControl>
                                      <Checkbox
                                        id={checkboxId}
                                        checked={field.value?.includes(
                                          option.value,
                                        )}
                                        onCheckedChange={(checked) => {
                                          const current = field.value || [];
                                          if (checked) {
                                            field.onChange([
                                              ...current,
                                              option.value,
                                            ]);
                                          } else {
                                            field.onChange(
                                              current.filter(
                                                (v) => v !== option.value,
                                              ),
                                            );
                                          }
                                        }}
                                        className="mb-0"
                                      />
                                    </FormControl>
                                    <Label
                                      htmlFor={checkboxId}
                                      className="cursor-pointer font-normal"
                                    >
                                      {option.label}
                                    </Label>
                                  </FormItem>
                                );
                              }}
                            />
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />

                  <Separator
                    className="my-4 h-px bg-zinc-200"
                    orientation="horizontal"
                  />

                  {/* Status Filter */}
                  <FormField
                    control={form.control}
                    name="status"
                    render={() => (
                      <FormItem>
                        <FormLabel className="mb-3 block">Status</FormLabel>
                        <div className="space-y-3">
                          {statusOptions.map((option) => (
                            <FormField
                              key={option.value}
                              control={form.control}
                              name="status"
                              render={({ field }) => {
                                const checkboxId = `status-${option.value}`;
                                return (
                                  <FormItem className="flex items-center gap-2">
                                    <FormControl>
                                      <Checkbox
                                        id={checkboxId}
                                        checked={field.value?.includes(
                                          option.value,
                                        )}
                                        onCheckedChange={(checked) => {
                                          const current = field.value || [];
                                          if (checked) {
                                            field.onChange([
                                              ...current,
                                              option.value,
                                            ]);
                                          } else {
                                            field.onChange(
                                              current.filter(
                                                (v) => v !== option.value,
                                              ),
                                            );
                                          }
                                        }}
                                        className="mb-0"
                                      />
                                    </FormControl>
                                    <Label
                                      htmlFor={checkboxId}
                                      className="cursor-pointer font-normal"
                                    >
                                      {option.label}
                                    </Label>
                                  </FormItem>
                                );
                              }}
                            />
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>

              <SheetFooter>
                <Button type="submit" variant="primary">
                  Apply Filters
                </Button>
                <Button type="button" variant="secondary" onClick={handleClear}>
                  Clear All
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
